import { Select } from 'antd';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useRef,
} from 'react';
import useModelInfrastructure from '@/hooks/useModelInfrastructure';

type Props = {
  inpId: number;
  typeId: number;
  onSelect: (value: any) => void;
  defaultValue?: any;
  excludeAssets?: string[];
  assetType?: string | null;
};

const SelectAsset = forwardRef<any, Props>(
  ({ inpId, typeId, onSelect, defaultValue = null, excludeAssets = [], assetType = null }, ref) => {
    const [selectedValue, setSelectedValue] = useState<any>(defaultValue);
    const [searchTerm, setSearchTerm] = useState<string>('');

    const debounceRef = useRef<number | null>(null);

    const { assets, setParams } = useModelInfrastructure({
      inpId,
      typeId,
      name: null,
      assetType,
    });

    useImperativeHandle(ref, () => ({
      setValue: (value: any) => {
        setSelectedValue(value);
      },
    }));

    useEffect(() => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(() => {
        if (searchTerm.length >= 1) {
          setParams((p: any) => ({ ...p, name: searchTerm, page: 1 }));
        } else if (searchTerm.length === 0) {
          setParams((p: any) => ({ ...p, name: null, page: 1 }));
        }
      }, 500);

      return () => {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
      };
    }, [searchTerm, setParams]);

    useEffect(() => {
      setParams((p: any) => ({ ...p, inpId, typeId, assetType }));
    }, [inpId, typeId, assetType]);

    useEffect(() => {
      // Clear selected value when typeId changes, but preserve defaultValue if it exists
      if (defaultValue && typeId) {
        setSelectedValue(defaultValue);
      } else {
        setSelectedValue(null);
        onSelect(null);
      }
    }, [typeId, defaultValue]);

    useEffect(() => {
      // Update selected value when defaultValue changes
      if (defaultValue !== null && defaultValue !== undefined) {
        setSelectedValue(defaultValue);
      }
    }, [defaultValue]);

    const handleChange = (val: string) => {
      setSelectedValue(val);
      onSelect(val);
      setSearchTerm('');
    };

    const handleSearch = (value: string) => {
      setSearchTerm(value);
    };

    return (
      <Select
        showSearch
        filterOption={false}
        placeholder="Select an option"
        value={selectedValue}
        defaultValue={defaultValue}
        style={{ width: '100%' }}
        onSearch={handleSearch}
        onChange={handleChange}
        notFoundContent={<span>No records found</span>}
        allowClear
        options={assets
          .filter((asset: any) => !excludeAssets.includes(asset))
          .map((asset: any) => ({
            value: asset,
            label: asset,
          }))}
      />
    );
  },
);

export default SelectAsset;
